import { useEffect, useRef } from "react";
import { useMarketplaceStore, MarketplaceConfig, MarketplaceState } from "@/store/marketplace-store";
import { useTCPParams } from "@/store/tcp-params-store";

/**
 * Default cache expiration time (1 hour)
 * This is also defined in the store, but exposed here for flexibility
 */
const CACHE_EXPIRATION = 60 * 60 * 1000;

/**
 * Custom hook for accessing and managing marketplace configuration
 * Provides a simpler interface to the marketplace store
 * 
 * @param autoFetch Whether to automatically fetch the configuration on mount
 * @returns The marketplace store and additional utility methods
 */
export function useMarketplace(autoFetch = true) {
  const store = useMarketplaceStore();
  const { params: tcpParams } = useTCPParams();
  const fetchAttemptedRef = useRef(false);

  // Add effect to set initial location from TCP params
  useEffect(() => {
    if (tcpParams.lat && tcpParams.long && !store.location) {
      store.setLocation(Number(tcpParams.lat), Number(tcpParams.long), tcpParams.city || '');
    }
  }, [tcpParams.lat, tcpParams.long, tcpParams.city, store]);
  
  useEffect(() => {
    // Skip fetching if this component has already attempted a fetch
    // This prevents redundant API calls when the component remounts
    if (fetchAttemptedRef.current) {
      return;
    }
    
    if (autoFetch) {
      // Set flag to indicate a fetch was attempted by this component
      fetchAttemptedRef.current = true;
      
      // Only fetch if:
      // 1. We don't have a config yet, OR
      // 2. The cache is expired, AND
      // 3. We're not already loading, AND
      // 4. fetchConfig is available
      const shouldFetch = 
        !store.config || 
        !store.lastUpdated || 
        Date.now() - store.lastUpdated > CACHE_EXPIRATION;
        
      if (shouldFetch && !store.isLoading && typeof store.fetchConfig === 'function') {
        try {
          console.log('[useMarketplace] Fetching configuration (if cache invalid)');
          // We're passing bypassCache=false to let the store check the cache first
          store.fetchConfig(false);
        } catch (error) {
          console.error("Error fetching marketplace config:", error);
        }
      } else {
        console.log('[useMarketplace] Using existing configuration data (cache valid)');
      }
    }
  }, [autoFetch, store]);
  
  /**
   * Check if the configuration has a specific feature enabled
   * Shorthand for accessing feature flags in the configuration
   * 
   * @param featureName The name of the feature to check
   * @returns Whether the feature is enabled
   */
  const hasFeature = (featureName: string): boolean => {
    try {
      if (typeof store.getConfigItem === 'function') {
        return store.getConfigItem(`settings.features.${featureName}`, false);
      }
      return false;
    } catch (error) {
      console.error("Error checking feature:", error);
      return false;
    }
  };
  
  /**
   * Get a configuration value by path
   * Alias for getConfigItem from the store for convenience
   * 
   * @param path Dot notation path to the configuration item
   * @param defaultValue Default value if not found
   * @returns The configuration value or default
   */
  const getConfig = <T>(path: string, defaultValue: T): T => {
    try {
      if (typeof store.getConfigItem === 'function') {
        return store.getConfigItem(path, defaultValue);
      }
      return defaultValue;
    } catch (error) {
      console.error("Error getting config value:", error);
      return defaultValue;
    }
  };
  
  /**
   * Force refresh the configuration from the API
   * Completely bypasses cache checks and can force direct API call
   * @param forceDirect - If true, bypass mock API in development and call real API directly
   */
  const refreshConfig = async (forceDirect = false): Promise<void> => {
    try {
      console.log("%c[useMarketplace] Force refreshing configuration from API", "background: #FF9800; color: white; padding: 3px; border-radius: 2px;");
      console.log("%c[useMarketplace] Force direct API call?", "background: #FF9800; color: white; padding: 3px; border-radius: 2px;", forceDirect);
      
      // No need to clear the cache first - we'll update it with new data
      // This ensures we have fallback data if the API call fails
      
      // Then fetch fresh data with bypassCache=true to force API call
      if (typeof store.fetchConfig === 'function') {
        console.log("%c[useMarketplace] Fetching fresh configuration data with bypassCache=true", "background: #FF9800; color: white; padding: 3px; border-radius: 2px;");
        await store.fetchConfig(true, forceDirect); // Pass true for bypassCache and forceDirect if specified
        console.log("%c[useMarketplace] Fetch complete", "background: #FF9800; color: white; padding: 3px; border-radius: 2px;");
      } else {
        console.error("fetchConfig is not available in the store");
      }
    } catch (error) {
      console.error("Error refreshing marketplace config:", error);
    }
  };
  
  return {
    ...store,
    hasFeature,
    getConfig,
    refreshConfig,
    location: store.location || {
      lat: tcpParams.lat || '28.6139298',
      lng: tcpParams.long || '77.2088282'
    },
    tcpParams
  };
}
